import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY as GET_COMMUNITY_QUERY_KEY } from './useGetCommunity';

export type ApiRequest_Reject_Membership = {
  communityId: number;
  membershipId: number;
};

export type ApiResponse_Reject_Membership = {
  success: boolean;
  message: string;
};

const METHOD = 'POST';
const ENDPOINT = 'communities/{communityId}/members/{membershipId}/reject';

export const useRejectMembershipRequest = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ApiRequest_Reject_Membership) => {
      try {
        const data = await handleApiCall<
          ApiResponse_Reject_Membership,
          Record<string, never>
        >(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, {
            communityId: params.communityId.toString(),
            membershipId: params.membershipId.toString(),
          }),
          {}
        );

        return data;
      } catch (err) {
        enqueueSnackbar('Failed to reject membership request.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: (_, variables) => {
      enqueueSnackbar('Membership request rejected.', { variant: 'success' });
      
      // Invalidate community data to refresh member list
      queryClient.invalidateQueries({
        queryKey: GET_COMMUNITY_QUERY_KEY({ id: variables.communityId }),
      });
    },
  });
};